import { Button, Callout, Dialog, IconButton, Text, Tooltip } from '@radix-ui/themes';
import { Info, PencilIcon, UserIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import RoleCards from './RoleCards';
import { roleRank } from './roles';
import type { EditUserAccessGroupProps } from './types/editUserAccessGroupProps';

const EditUserAccessGroup = ({
  accessGroup,
  user,
  userLoading,
  onSubmit,
  allRoles,
  canAssignSuperAdmin,
  disable,
}: EditUserAccessGroupProps) => {
  const { t } = useTranslation();
  const roles = user?.roles ?? [];
  const currentRoleId = roles.find((r) => roleRank(r.key) >= 0)?.id ?? '';
  const oldRoleNames = roles.filter((r) => roleRank(r.key) < 0).map((r) => r.name);
  const memberIsAboveCurrentUser = user.isSuperAgAdmin && !canAssignSuperAdmin;

  const [roleId, setRoleId] = useState(currentRoleId);
  const userName = `${user.firstName} ${user.lastName}`;

  const tooltip = memberIsAboveCurrentUser
    ? t('settings.accessGroupv2.editUserAccessGroup.superAdminTooltip')
    : disable
      ? t('settings.accessGroupv2.editUserAccessGroup.noPermissionTooltip')
      : t('settings.accessGroupv2.editUserAccessGroup.editUserTooltip');

  return (
    <Dialog.Root onOpenChange={() => setRoleId(currentRoleId)}>
      <Tooltip content={<Text size="3">{tooltip}</Text>}>
        <Dialog.Trigger>
          <IconButton
            variant="ghost"
            color="gray"
            highContrast
            className="m-0"
            disabled={disable || memberIsAboveCurrentUser}
          >
            <UserIcon size={20} />
            <PencilIcon size={10} />
          </IconButton>
        </Dialog.Trigger>
      </Tooltip>
      <Dialog.Content>
        <Dialog.Title>{t('settings.accessGroupv2.editUserAccessGroup.dialogTitle')}</Dialog.Title>
        <Dialog.Description>
          {t('settings.accessGroupv2.editUserAccessGroup.description', { userName, groupName: accessGroup.name })}
        </Dialog.Description>
        {oldRoleNames.length > 0 && (
          <Callout.Root color="amber" size="1" className="mt-4">
            <Callout.Icon>
              <Info size={16} />
            </Callout.Icon>
            <Callout.Text>
              {t('settings.accessGroupv2.editUserAccessGroup.oldRolesWarning', {
                userName,
                roles: oldRoleNames.join(', '),
              })}
            </Callout.Text>
          </Callout.Root>
        )}
        <div className="pt-4">
          <RoleCards
            roles={allRoles}
            value={roleId}
            onValueChange={setRoleId}
            canAssignSuperAdmin={canAssignSuperAdmin}
          />
        </div>
        <div className="flex flex-row justify-between w-full pt-4">
          <Dialog.Close>
            <Button size="3" color="gray" variant="soft" className="w-full">
              {t('settings.accessGroupv2.editUserAccessGroup.cancelButton')}
            </Button>
          </Dialog.Close>
          <Dialog.Close>
            <Button
              size="3"
              color="amber"
              className="w-full"
              loading={userLoading}
              onClick={() => onSubmit(accessGroup.id, user.id, [roleId])}
              disabled={!roleId}
            >
              {t('settings.accessGroupv2.editUserAccessGroup.updateButton')}
            </Button>
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default EditUserAccessGroup;
